import { AtSign, Eye, EyeOff, Lock } from 'lucide-react';
import { useState, type CSSProperties, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { toAppError } from '../../api/errors';
import { useAuth } from '../../app/auth';
import { Routes } from '../../app/routes';
import logo from '../../assets/brand/logo.svg?raw';
import { useT } from '../../l10n/i18n';
import { errorMessage } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import './login.css';

// Sign-in. The web app talks to its own origin, so there is no server to choose (mobile only).

/** The whole team, gathered along an arc: the two who answer customers stand in the middle. */
const team = [
  { avatar: 'facebook_manager', color: '#3E63DD', x: -1.22, y: 0.05, size: 52 },
  { avatar: 'aso_analyst', color: '#12A594', x: -0.74, y: -0.3, size: 52 },
  { avatar: 'secretary', color: '#8E4EC6', x: -0.27, y: -0.5, size: 64 },
  { avatar: 'process_engineer', color: '#4E6A86', x: 0.27, y: -0.5, size: 64 },
  { avatar: 'sales', color: '#C08A00', x: 0.74, y: -0.3, size: 52 },
  { avatar: 'reviews', color: '#F76B15', x: 1.22, y: 0.05, size: 52 },
];

export function LoginPage() {
  const t = useT();
  const { status, signIn } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only paths inside the app, never another site.
  const from = params.get('from');
  const target = from?.startsWith('/') && !from.startsWith('//') && from !== '/' ? from : Routes.inbox;
  if (status === 'signedIn') return <Navigate to={target} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || !password) {
      setError(t('errorValidation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(errorMessage(t, toAppError(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login-form" onSubmit={submit} noValidate>
        <div className="login-team" aria-hidden>
          {team.map((m) => (
            <span
              key={m.avatar}
              className="login-member"
              style={
                {
                  left: `calc(50% + ${m.x * 112 - m.size / 2}px)`,
                  top: 50 + m.y * 70,
                  animationDelay: `${(m.x + 1) * 160}ms`,
                } as CSSProperties
              }
            >
              <EmployeeAvatar avatar={m.avatar} color={m.color} size={m.size} animate />
            </span>
          ))}
        </div>
        <h1 className="login-logo" aria-label={t('appTitle')} dangerouslySetInnerHTML={{ __html: logo }} />
        <p className="body-large muted pre-wrap login-tagline">{t('loginTagline')}</p>

        <label className="login-input">
          <AtSign size={20} aria-hidden />
          <input
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            placeholder={t('loginEmail')}
            aria-label={t('loginEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </label>
        <label className="login-input">
          <Lock size={20} aria-hidden />
          <input
            className="input"
            type={show ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            placeholder={t('loginPassword')}
            aria-label={t('loginPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="button" className="icon-btn" title={t('loginPassword')} onClick={() => setShow(!show)}>
            {show ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </label>

        <button type="submit" className="btn btn-filled btn-block" disabled={busy}>
          {t('loginButton')}
        </button>
        {error && (
          <p role="alert" className="body-medium login-error">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}
